"use client";
import { env } from "@repo/env";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { httpBatchLink, httpSubscriptionLink, splitLink } from "@trpc/client";
import React, { useState } from "react";

import getQueryClient from "@/components/providers/tanstack-query";

import { EdgeHttpError, parseRetryAfterMs } from "./edge-errors";
import { getPostHogSessionHeader } from "./posthog-session-header";
import { api } from "./react";
import { getClientTimezoneHeader } from "./timezone-header";

function getBaseUrl() {
  if (typeof window !== "undefined") {
    return "";
  }
  if (env.VERCEL_URL) {
    return `https://${env.VERCEL_URL}`;
  }
  return `http://localhost:${env.PORT ?? 3000}`;
}

export default function TRPCProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const queryClient = getQueryClient();
  const [trpcClient] = useState(() =>
    api.createClient({
      links: [
        splitLink({
          condition(op) {
            // Use subscription link for subscriptions, batch link for everything else
            return op.type === "subscription";
          },
          true: httpSubscriptionLink({
            url: `${getBaseUrl()}/api/trpc`,
          }),
          false: httpBatchLink({
            url: `${getBaseUrl()}/api/trpc`,
            headers: () => ({
              ...getClientTimezoneHeader(),
              ...getPostHogSessionHeader(),
            }),
            async fetch(url, options) {
              const response = await fetch(url, {
                ...options,
                signal: AbortSignal.timeout(30000), // 30 second timeout
              });
              // Vercel's edge/platform rate-limits request bursts with a
              // *plaintext* body ("Too Many Requests"). tRPC would JSON.parse
              // it and throw an opaque "Unexpected token" error, and the query
              // client couldn't tell it was a transient 429 to back off on.
              // Convert any non-JSON error response into a typed error the
              // retry policy understands (see edge-errors.ts). tRPC-shaped
              // errors (JSON, including our own TOO_MANY_REQUESTS middleware)
              // pass through untouched so error.data.code/httpStatus keep
              // working downstream.
              if (!response.ok) {
                const contentType = response.headers.get("content-type") ?? "";
                if (!contentType.includes("application/json")) {
                  throw new EdgeHttpError(
                    response.status,
                    parseRetryAfterMs(response.headers.get("retry-after")),
                  );
                }
              }
              return response;
            },
          }),
        }),
      ],
    }),
  );

  return (
    <api.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        {children}
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </api.Provider>
  );
}
